import { Card, CbhIcon, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { ExternalLink } from "@src/appV2/lib";
import { formatDistanceInMiles } from "@src/appV2/lib/utils/distance";

import { useOpenGeoLocation } from "../../OpenGeoLocation/useOpenGeoLocation";
import { type PlacementDetail } from "../api/useGetPlacement";
import { type AlternativeAddressData } from "./PlacementDetailsPage/hooks/useAlternativeAddress";
import { PlacementLocationMap } from "./PlacementLocationMap";

interface AlternativeAddressInfoProps {
  placementDetails: PlacementDetail;
  alternativeAddressData: Omit<AlternativeAddressData, "description">;
}

export function AlternativeAddressInfo(props: AlternativeAddressInfoProps) {
  const { placementDetails, alternativeAddressData } = props;
  const { coordinates, formattedAddress, distance } = alternativeAddressData;

  const { googleMapsHref: alternativeAddressGoogleMapsHref } = useOpenGeoLocation({
    location: {
      lat: coordinates.latitude,
      lng: coordinates.longitude,
    },
    formattedAddress,
  });

  const { googleMapsHref: placementGoogleMapsHref } = useOpenGeoLocation({
    location: {
      lat: placementDetails.coordinates[1],
      lng: placementDetails.coordinates[0],
    },
    formattedAddress: placementDetails.address.formatted ?? "",
  });

  return (
    <Stack spacing="28px">
      <Stack spacing={7}>
        <Text variant="h4" align="left" sx={{ fontWeight: 500 }}>
          Heads up!
        </Text>
        <Stack spacing={5}>
          <Text variant="body1" align="left" sx={{ lineHeight: 1.3 }}>
            The interview will take place at{" "}
            <ExternalLink
              to={alternativeAddressGoogleMapsHref ?? ""}
              sx={{ color: "inherit", fontSize: "inherit" }}
            >
              {formattedAddress}
            </ExternalLink>
            .
          </Text>
        </Stack>
      </Stack>
      <Stack spacing={7} sx={{ flex: 1 }}>
        {isDefined(distance) && (
          <Stack spacing={5} flex={1} direction="row" alignItems="center">
            <CbhIcon type="car-filled" size="medium" />
            <Text semibold variant="h6">
              {formatDistanceInMiles(distance, "long")} from home
            </Text>
          </Stack>
        )}

        <Card>
          <PlacementLocationMap
            coordinates={[coordinates.longitude, coordinates.latitude]}
            formattedAddress={formattedAddress}
            placementId={placementDetails.id}
          />
        </Card>
      </Stack>
      <Stack spacing={5}>
        <Text variant="body1" align="left">
          You only have to go to this address for the interview.
        </Text>
        <Text
          variant="body2"
          align="left"
          sx={{ color: (theme) => theme.palette.text.secondary, lineHeight: 1.3 }}
        >
          The job itself will take place at{" "}
          <ExternalLink
            to={placementGoogleMapsHref ?? ""}
            sx={{ color: "inherit", fontSize: "inherit" }}
          >
            {placementDetails.address.formatted ?? ""}
          </ExternalLink>
          .
        </Text>
        <Text
          variant="body2"
          align="left"
          sx={{ color: (theme) => theme.palette.text.secondary, lineHeight: 1.3 }}
        >
          If you have any questions on the interview location, please reach out to support at{" "}
          <ExternalLink to="tel:8624128337">(862) 412-8337</ExternalLink>.
        </Text>
      </Stack>
    </Stack>
  );
}
